import "./style.css";
import { config } from "./config";
import { Device } from "./core/Device";
import { QualityManager } from "./core/Quality";
import { Stage } from "./core/Stage";
import { Game } from "./core/Game";
import { Loader } from "./core/Loader";
import type { AssetEntry } from "./core/Loader";
import { InputManager } from "./core/input/InputManager";
import { KeyboardMouse } from "./core/input/KeyboardMouse";
import { Touch } from "./core/input/Touch";
import { CameraRig } from "./core/camera/CameraRig";
import { OrbitCameraController } from "./core/camera/controllers/OrbitCameraController";
import { World } from "./world/World";
import { Environment } from "./world/Environment";
import { HUD } from "./ui/HUD";
import { requireElement } from "./utils/dom";
import type { Debug } from "./core/Debug";
import type { Screen, ScreenContext, ScreenName } from "./types/screen";
import type { MatchSetup } from "./types/match";

/** Aucun asset pour l'instant : le Loader émet `ready` immédiatement. */
const MANIFEST: readonly AssetEntry[] = [];

const container = requireElement(config.containerSelector);

const device = new Device();
const quality = new QualityManager(device.tier, device.pixelRatio);
const stage = new Stage({ container, settings: quality.settings });
quality.events.on("change", ({ settings }) => stage.applyQuality(settings));

const hud = new HUD(container);
const input = new InputManager();
input.addSource(new KeyboardMouse(stage.renderer.domElement));

// Un doigt déplace, deux doigts tournent, inclinent et zooment ([GD-045]).
if (device.hasTouch)
  input.addSource(new Touch({ lookTarget: stage.renderer.domElement }));

const cameraRig = new CameraRig(stage.camera);
const cameraController = new OrbitCameraController(stage.camera);
cameraRig.setController(cameraController);

const world = new World(stage.scene);
const game = new Game();
let debug: Debug | null = null;
let screen: Screen | null = null;

/**
 * Ce que la coquille prête à chaque page. Elle ne sait rien de ce qu'elles en
 * font : une page possède ce qu'elle crée et le libère en partant ([GD-036]).
 */
const context: ScreenContext = {
  hud: hud.element,
  quality,
  device,
  world,
  camera: stage.camera,
  cameraController,
  go: (name) => {
    void show(name);
  },
  start: (setup) => {
    void show("match", setup);
  },
};

/** Charge une page en import dynamique et libère la précédente. */
const show = async (name: ScreenName, setup?: MatchSetup): Promise<void> => {
  screen?.dispose();
  screen = null;
  if (name === "home") {
    const { HomeScreen } = await import("./screens/HomeScreen");
    screen = new HomeScreen(context);
  } else if (name === "setup") {
    const { SetupScreen } = await import("./screens/SetupScreen");
    screen = new SetupScreen(context);
  } else if (name === "editor") {
    const { EditorScreen } = await import("./screens/EditorScreen");
    screen = new EditorScreen(context);
  } else if (setup) {
    const { MatchScreen } = await import("./screens/MatchScreen");
    screen = new MatchScreen(context, setup);
  }
};

game.events.on("tick", ({ dt }) => {
  debug?.beginFrame();
  input.update(dt);
  cameraRig.update(input.state, dt);
  screen?.update?.(input.state, dt);
  world.update(dt);
  stage.render();
  debug?.endFrame();
});

const loader = new Loader();
loader.events.once("ready", () => {
  void build();
});
loader.load(MANIFEST);

/** Construction du monde : synchrone, une fois les assets disponibles. */
async function build(): Promise<void> {
  world.add(new Environment(quality.settings));
  if (config.debug) {
    const { Debug } = await import("./core/Debug");
    debug = await Debug.create(container);
  }
  await show("home");
  game.start();
}

const dispose = (): void => {
  game.dispose();
  input.dispose();
  cameraRig.dispose();
  screen?.dispose();
  screen = null;
  world.dispose();
  loader.dispose();
  debug?.dispose();
  hud.dispose();
  quality.dispose();
  stage.dispose();
};

window.addEventListener("beforeunload", dispose, { once: true });

if (import.meta.hot) import.meta.hot.dispose(dispose);
